import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { DEFAULT_LOGIN_APPEARANCE, mergeAppearance } from '../utils/loginAppearance';

const REMEMBER_KEY = 'gp_admin_remember';

export default function Login({ onLogin }) {
  const [appearance, setAppearance] = useState(DEFAULT_LOGIN_APPEARANCE);
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => {
    try { return !!localStorage.getItem(REMEMBER_KEY); } catch { return false; }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [slide, setSlide] = useState(0);

  // ── Mobile OTP login ── (the only supported login method)
  const [mode, setMode] = useState('otp');   // 'password' | 'otp'
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [info, setInfo] = useState('');
  const [resendIn, setResendIn] = useState(0);

  // Countdown for the "Resend OTP" link.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const switchMode = (next) => {
    setMode(next); setError(''); setInfo('');
    setOtp(''); setOtpSent(false); setResendIn(0);
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    const m = mobile.replace(/\D/g, '');
    if (!/^[6-9]\d{9}$/.test(m)) { setError('Please enter a valid 10-digit mobile number'); return; }
    setLoading(true); setError(''); setInfo('');
    try {
      const res = await api.sendLoginOtp(m);
      if (res?.success) {
        setOtpSent(true); setResendIn(30);
        setInfo(res.message || 'OTP sent to your mobile number');
      } else {
        setError(res?.error || 'Could not send OTP');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    const m = mobile.replace(/\D/g, '');
    const code = otp.replace(/\D/g, '');
    if (code.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setLoading(true); setError(''); setInfo('');
    try {
      const res = await api.verifyLoginOtp(m, code);
      if (res?.success) {
        onLogin({ user: res.user, token: res.token });
      } else {
        setError(res?.error || 'OTP verification failed');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Load admin-configured appearance (falls back to defaults) ──
  useEffect(() => {
    let alive = true;
    api.settings().then(res => {
      if (alive && res?.value) setAppearance(mergeAppearance(res.value));
    }).catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);

  // ── Background carousel ──
  const images = appearance.images;
  const intervalRef = useRef(null);
  useEffect(() => {
    if (images.length <= 1 || !appearance.carouselInterval) return;
    intervalRef.current = setInterval(
      () => setSlide(s => (s + 1) % images.length),
      appearance.carouselInterval * 1000
    );
    return () => clearInterval(intervalRef.current);
  }, [images.length, appearance.carouselInterval]);

  // Keep the active slide index valid if the image list changes.
  useEffect(() => { setSlide(s => (s >= images.length ? 0 : s)); }, [images.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) { setError('Please fill in both fields'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.login(username.trim(), password);
      if (res?.success) {
        try {
          if (remember) localStorage.setItem(REMEMBER_KEY, username.trim());
          else localStorage.removeItem(REMEMBER_KEY);
        } catch { /* ignore */ }
        onLogin({ user: res.user, token: res.token });
      } else {
        setError(res?.error || 'Login failed');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const a = appearance;
  const accent = `linear-gradient(135deg, ${a.primaryColor}, ${a.primaryColorDark})`;

  return (
    <div className="login-page">
      <div className="login-shell">
        {/* ── Left: showcase / carousel ── */}
        <div className="login-left">
          {images.map((src, i) => (
            <div
              key={src + i}
              className="login-slide"
              style={{ backgroundImage: `url(${src})`, opacity: i === slide ? 1 : 0 }}
            />
          ))}
          <div className="login-left-overlay" style={{ background:
            `linear-gradient(180deg, rgba(0,0,0,${a.panelOverlay * 0.5}) 0%, rgba(0,0,0,${a.panelOverlay}) 60%, rgba(0,0,0,${Math.min(1, a.panelOverlay + 0.25)}) 100%)` }} />

          <div className="left-content">
            <h2 className="panel-title">{a.panelTitle}</h2>
            {a.panelSubtitle && <p className="panel-subtitle">{a.panelSubtitle}</p>}
            {images.length > 1 && (
              <div className="login-dots">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => setSlide(i)}
                    className={`login-dot${i === slide ? ' is-active' : ''}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: login form ── */}
        <div className="login-right">
          <form className="login-form" onSubmit={mode === 'otp' ? (otpSent ? verifyOtp : sendOtp) : submit}>
            <div className="login-form-brand">
              {/* Login logo is fixed to the bundled gpp-logo.png (not the
                  admin-configurable logoUrl), so it can't be overridden. */}
              <img src="/gpp-logo.png" alt={a.brandName} className="login-form-logo"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            {/* Tagline ("Fair Policy | Fair Property") intentionally removed by
                request, so a saved Settings value can't bring it back. */}
            <h1 className="login-heading">{a.heading}</h1>
            {/* Subheading intentionally not rendered (removed by request),
                so a saved Settings value can't bring it back. */}

            {/* Login is restricted to Mobile OTP authentication only — the
                password method and its method tabs were removed by request. */}

            {mode === 'password' ? (
              <>
                <label className="login-label">{a.usernameLabel}</label>
                <input
                  className="login-input"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoFocus={!username}
                  autoComplete="username"
                  disabled={loading}
                />

                <label className="login-label">{a.passwordLabel}</label>
                <div className="login-pwd-wrap">
                  <input
                    className="login-input login-input--pwd"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="login-eye"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className="login-label">Mobile Number</label>
                <input
                  className="login-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={mobile}
                  onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter your 10-digit mobile number"
                  autoFocus
                  disabled={loading || otpSent}
                />

                {otpSent && (
                  <>
                    <label className="login-label">Enter OTP</label>
                    <input
                      className="login-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit code"
                      autoFocus
                      disabled={loading}
                    />
                    <div className="login-otp-actions">
                      <button
                        type="button"
                        className="login-link"
                        style={{ color: a.primaryColor }}
                        onClick={() => switchMode('otp')}
                        disabled={loading}
                      >
                        Change number
                      </button>
                      <button
                        type="button"
                        className="login-link"
                        style={{ color: resendIn > 0 ? '#999' : a.primaryColor }}
                        onClick={sendOtp}
                        disabled={loading || resendIn > 0}
                      >
                        {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {mode === 'password' && (a.showRememberMe || a.showForgotPassword) && (
              <div className="login-options">
                {a.showRememberMe ? (
                  <label className="login-remember">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={e => setRemember(e.target.checked)}
                      style={{ accentColor: a.primaryColor }}
                    />
                    Remember Me
                  </label>
                ) : <span />}
                {a.showForgotPassword && (
                  <a
                    href={a.forgotPasswordUrl || '#'}
                    className="login-forgot"
                    style={{ color: a.primaryColor }}
                    onClick={e => { if (!a.forgotPasswordUrl) e.preventDefault(); }}
                  >
                    {a.forgotPasswordText}
                  </a>
                )}
              </div>
            )}

            {error && <div className="login-error">{error}</div>}
            {!error && info && <div className="login-info">{info}</div>}

            <button
              type="submit"
              className="login-btn"
              style={{ background: accent }}
              disabled={loading}
            >
              {mode === 'otp'
                ? (loading ? (otpSent ? 'Verifying…' : 'Sending OTP…') : (otpSent ? 'Verify & Sign in' : 'Send OTP'))
                : (loading ? 'Signing in…' : a.buttonText)}
            </button>

            {a.footerText && <div className="login-footer">{a.footerText}</div>}
          </form>
        </div>
      </div>
    </div>
  );
}

